"use client";

import { useState, type KeyboardEvent, type MouseEvent } from "react";
import { format, isValid, parse, startOfDay } from "date-fns";
import { Calendar } from "@/components/ui/Calendar";
import { Icon } from "@/components/ui/Icon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  "aria-label"?: string;
  className?: string;
  disabled?: boolean;
  /** Blocks picking a day before today — for check-in style fields where a past stay date makes no sense. */
  disablePast?: boolean;
  /** Blocks picking a day before this ISO date — e.g. check-out can't precede check-in. */
  minDate?: string;
}

function parseISODate(value: string): Date | undefined {
  if (!value) return undefined;
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : undefined;
}

function toISODate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/**
 * Date field with shadcn Calendar in a popover.
 * Value is stored as `yyyy-MM-dd` (API-friendly); display uses `dd/MM/yyyy`.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = "dd/mm/yyyy",
  "aria-label": ariaLabel,
  className,
  disabled = false,
  disablePast = false,
  minDate,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parseISODate(value);
  const minDateObj = minDate ? parseISODate(minDate) : undefined;
  const disabledMatchers = [
    disablePast ? { before: startOfDay(new Date()) } : null,
    minDateObj ? { before: minDateObj } : null,
  ].filter((matcher): matcher is { before: Date } => matcher !== null);

  const clear = (event?: MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    onChange("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "inline-flex h-[46px] min-w-[10.5rem] items-center justify-between gap-2 rounded-xl border border-secondary/10 bg-card px-3 text-sm outline-none transition",
            "hover:border-secondary/25 focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/15",
            selected ? "text-secondary" : "text-muted",
            disabled ? "opacity-50 cursor-not-allowed" : "",
            className,
          )}
        >
          <span>
            {selected ? format(selected, "dd/MM/yyyy") : placeholder}
          </span>
          {selected ? (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear date"
              className="rounded-md p-0.5 text-muted hover:bg-secondary/10 hover:text-secondary"
              onClick={clear}
              onKeyDown={(event: KeyboardEvent) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  clear();
                }
              }}
            >
              <Icon name="close" size={14} />
            </span>
          ) : (
            <Icon name="calendar" size={16} className="shrink-0 text-muted" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          required={false}
          selected={selected}
          disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
          onSelect={(date) => {
            if (!date || (value && toISODate(date) === value)) {
              onChange("");
            } else {
              onChange(toISODate(date));
            }
            setOpen(false);
          }}
          defaultMonth={selected}
        />
        {selected ? (
          <div className="border-t border-secondary/10 px-3 py-2">
            <button
              type="button"
              className="text-xs font-medium text-muted hover:text-secondary"
              onClick={() => clear()}
            >
              Clear date
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
